import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography,
  Box,
  Divider,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import SendIcon from "@material-ui/icons/Send";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import useSocketSubscription from "../../hooks/useSocketSubscription";
import copyToClipboard from "../../utils/copyToClipboard";

const useStyles = makeStyles((theme) => ({
  breakdown: {
    backgroundColor: "#f4f6f9",
    borderRadius: 6,
    padding: theme.spacing(2),
  },
  breakdownRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "4px 0",
    fontSize: 14,
  },
  breakdownStrong: {
    fontWeight: 700,
    fontSize: 15,
    color: "#222",
  },
  riskHigh: { color: "#d32f2f" },
  riskMedium: { color: "#ed6c02" },
  riskLow: { color: "#2e7d32" },
  warningBox: {
    backgroundColor: "#fff3cd",
    border: "1px solid #ffe69c",
    color: "#664d03",
    padding: theme.spacing(1.5),
    borderRadius: 6,
    fontSize: 13,
    marginBottom: theme.spacing(2),
  },
  generatedBox: {
    backgroundColor: "#e8f5e9",
    border: "1px solid #a5d6a7",
    padding: theme.spacing(1.5),
    borderRadius: 6,
    marginBottom: theme.spacing(2),
  },
}));

const METHODS = [
  { value: "pix", label: "PIX" },
  { value: "boleto", label: "Boleto bancário" },
  { value: "credit_card", label: "Cartão de crédito" },
  { value: "debit_card", label: "Cartão de débito" },
];

const fmtBRL = (n) =>
  Number(n || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const debounce = (fn, ms) => {
  let t = null;
  return (...args) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
};

const PaymentLinkModal = ({ open, onClose, ticketId }) => {
  const classes = useStyles();
  const [gateways, setGateways] = useState([]);
  const [form, setForm] = useState({
    gatewayId: "",
    paymentMethod: "pix",
    installments: 1,
    originalAmount: "",
    discountAmount: "0",
    additionalAmount: "0",
    taxAmount: "0",
    title: "",
    description: "",
    expiresAt: "",
    payerName: "",
    payerDocument: "",
    payerEmail: "",
    notes: "",
    linkToReceivable: false,
  });
  const [simulation, setSimulation] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null);
  // O backend ja emitia `payment-link` quando o webhook do gateway muda o
  // status, mas ninguem escutava: o operador mandava o link e so descobria o
  // pagamento recarregando a tela. Aqui a cobranca gerada se atualiza sozinha.
  useSocketSubscription(
    "payment-link",
    (data) => {
      if (data?.action !== "status_changed") return;
      setGenerated((atual) =>
        atual && atual.id === data.chargeId
          ? { ...atual, status: data.newStatus, paidAt: data.paidAt }
          : atual
      );
    },
    [ticketId]
  );
  const [message, setMessage] = useState("");

  // Carrega gateways ativos
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/payment-gateways");
        const actives = (data || []).filter((g) => g.active);
        setGateways(actives);
        if (actives.length > 0) {
          setForm((f) => ({ ...f, gatewayId: actives[0].id }));
        }
      } catch (err) {
        toastError(err);
      }
    })();
  }, [open]);

  // Reseta ao abrir/fechar
  useEffect(() => {
    if (open) return;
    setSimulation(null);
    setGenerated(null);
    setMessage("");
  }, [open]);

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Simulacao automatica (debounced) quando inputs mudam
  const runSimulate = useCallback(
    async (snapshot) => {
      if (!snapshot.gatewayId) return;
      const original = Number(snapshot.originalAmount);
      if (!original || original <= 0) {
        setSimulation(null);
        return;
      }
      setSimulating(true);
      try {
        const { data } = await api.post(
          `/tickets/${ticketId}/payment-links/simulate`,
          {
            gatewayId: snapshot.gatewayId,
            paymentMethod: snapshot.paymentMethod,
            installments:
              snapshot.paymentMethod === "credit_card"
                ? Number(snapshot.installments) || 1
                : 1,
            originalAmount: original,
            discountAmount: Number(snapshot.discountAmount) || 0,
            additionalAmount: Number(snapshot.additionalAmount) || 0,
            taxAmount: Number(snapshot.taxAmount) || 0,
          }
        );
        setSimulation(data);
      } catch (err) {
        // 400 com mensagem do backend (ex: ERR_INVALID_AMOUNT) — mostra
        // sem stack pra nao poluir.
        const msg = err?.response?.data?.error;
        if (msg) {
          setSimulation({ __error: msg });
        } else {
          toastError(err);
        }
      } finally {
        setSimulating(false);
      }
    },
    [ticketId]
  );

  // debounced ref que segue o estado mais recente do form
  const debouncedSimulate = useMemo(
    () => debounce((snapshot) => runSimulate(snapshot), 400),
    [runSimulate]
  );

  useEffect(() => {
    if (!open || generated) return;
    debouncedSimulate(form);
  }, [form, open, generated, debouncedSimulate]);

  const riskLabel = useMemo(() => {
    if (!simulation || simulation.__error) return null;
    const settlement = simulation.settlementDays;
    if (settlement >= 30) return { text: "Recebimento lento", cls: classes.riskHigh };
    if (settlement >= 2) return { text: "Recebimento médio", cls: classes.riskMedium };
    return { text: "Recebimento rápido", cls: classes.riskLow };
  }, [simulation, classes]);

  const handleGenerate = async () => {
    if (!simulation || simulation.__error) {
      toast.error("Aguarde a simulação ficar válida antes de gerar.");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post(`/tickets/${ticketId}/payment-links`, {
        gatewayId: form.gatewayId,
        paymentMethod: form.paymentMethod,
        installments:
          form.paymentMethod === "credit_card"
            ? Number(form.installments) || 1
            : 1,
        originalAmount: Number(form.originalAmount),
        discountAmount: Number(form.discountAmount) || 0,
        additionalAmount: Number(form.additionalAmount) || 0,
        taxAmount: Number(form.taxAmount) || 0,
        title: form.title || null,
        description: form.description || null,
        expiresAt: form.expiresAt || null,
        payerName: form.payerName || null,
        payerDocument: form.payerDocument || null,
        payerEmail: form.payerEmail || null,
        notes: form.notes || null,
        linkToReceivable: !!form.linkToReceivable,
      });
      setGenerated(data.charge);
      setMessage(data.message || "");
      toast.success("Link de pagamento gerado!");
    } catch (err) {
      toastError(err);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyMessage = async () => {
    if (!message) return;
    if (await copyToClipboard(message)) toast.info("Mensagem copiada");
    else toast.error("Não foi possível copiar. Selecione o texto e copie à mão.");
  };

  const handleInsertInChat = () => {
    if (!message) return;
    window.dispatchEvent(
      new CustomEvent("ai-assist:set-input", {
        detail: { text: message, ticketId },
      })
    );
    toast.success("Mensagem inserida no campo do chat — revise e envie");
    onClose && onClose();
  };

  const handleCopyLink = async () => {
    if (!generated?.paymentLink) return;
    if (await copyToClipboard(generated.paymentLink)) toast.info("Link copiado");
    else toast.error("Não foi possível copiar. Selecione o link e copie à mão.");
  };

  const noGateway = gateways.length === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Gerar pagamento</DialogTitle>
      <DialogContent dividers>
        {!generated && (
          <div className={classes.warningBox}>
            Mensagens com link de pagamento devem ser enviadas apenas para
            clientes com relacionamento legítimo. Confira valor e dados antes
            de gerar.
          </div>
        )}

        {noGateway && (
          <Typography color="error" variant="body2" style={{ marginBottom: 16 }}>
            Nenhum gateway de pagamento ativo encontrado. Configure em
            /settings antes de gerar links.
          </Typography>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Gateway / conta"
              value={form.gatewayId || ""}
              onChange={(e) => updateForm("gatewayId", e.target.value)}
              variant="outlined"
              size="small"
              disabled={!!generated || noGateway}
            >
              {gateways.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.name} ({g.provider}
                  {g.sandbox ? " sandbox" : ""})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Forma de pagamento"
              value={form.paymentMethod}
              onChange={(e) => updateForm("paymentMethod", e.target.value)}
              variant="outlined"
              size="small"
              disabled={!!generated}
            >
              {METHODS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              label="Valor base"
              type="number"
              value={form.originalAmount}
              onChange={(e) => updateForm("originalAmount", e.target.value)}
              variant="outlined"
              size="small"
              inputProps={{ min: 0, step: 0.01 }}
              disabled={!!generated}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              label="Desconto"
              type="number"
              value={form.discountAmount}
              onChange={(e) => updateForm("discountAmount", e.target.value)}
              variant="outlined"
              size="small"
              inputProps={{ min: 0, step: 0.01 }}
              disabled={!!generated}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              label="Acréscimo"
              type="number"
              value={form.additionalAmount}
              onChange={(e) => updateForm("additionalAmount", e.target.value)}
              variant="outlined"
              size="small"
              inputProps={{ min: 0, step: 0.01 }}
              disabled={!!generated}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              label="Imposto estimado"
              type="number"
              value={form.taxAmount}
              onChange={(e) => updateForm("taxAmount", e.target.value)}
              variant="outlined"
              size="small"
              inputProps={{ min: 0, step: 0.01 }}
              disabled={!!generated}
            />
          </Grid>

          {form.paymentMethod === "credit_card" && (
            <Grid item xs={6} sm={3}>
              <TextField
                select
                fullWidth
                label="Parcelas"
                value={form.installments}
                onChange={(e) => updateForm("installments", e.target.value)}
                variant="outlined"
                size="small"
                disabled={!!generated}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}x
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={12} sm={form.paymentMethod === "credit_card" ? 9 : 12}>
            <TextField
              fullWidth
              label="Título / produto"
              value={form.title}
              onChange={(e) => updateForm("title", e.target.value)}
              variant="outlined"
              size="small"
              disabled={!!generated}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Descrição (mostrada ao cliente)"
              value={form.description}
              onChange={(e) => updateForm("description", e.target.value)}
              variant="outlined"
              size="small"
              multiline
              minRows={2}
              disabled={!!generated}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Vencimento / validade"
              InputLabelProps={{ shrink: true }}
              value={form.expiresAt}
              onChange={(e) => updateForm("expiresAt", e.target.value)}
              variant="outlined"
              size="small"
              disabled={!!generated}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.linkToReceivable}
                  onChange={(e) =>
                    updateForm("linkToReceivable", e.target.checked)
                  }
                  disabled={!!generated}
                />
              }
              label="Criar conta a receber vinculada (financeiro)"
            />
          </Grid>
        </Grid>

        {/* Breakdown da simulação */}
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Simulação financeira
            {simulating && (
              <CircularProgress
                size={14}
                style={{ marginLeft: 8, verticalAlign: "middle" }}
              />
            )}
          </Typography>
          {simulation?.__error && (
            <Typography variant="body2" color="error">
              {simulation.__error}
            </Typography>
          )}
          {simulation && !simulation.__error && (
            <Box className={classes.breakdown}>
              <div className={classes.breakdownRow}>
                <span>Valor base</span>
                <span>{fmtBRL(simulation.originalAmount)}</span>
              </div>
              {simulation.discountAmount > 0 && (
                <div className={classes.breakdownRow}>
                  <span>Desconto</span>
                  <span>− {fmtBRL(simulation.discountAmount)}</span>
                </div>
              )}
              {simulation.additionalAmount > 0 && (
                <div className={classes.breakdownRow}>
                  <span>Acréscimo</span>
                  <span>+ {fmtBRL(simulation.additionalAmount)}</span>
                </div>
              )}
              <Divider style={{ margin: "6px 0" }} />
              <div
                className={`${classes.breakdownRow} ${classes.breakdownStrong}`}
              >
                <span>Valor final cobrado do cliente</span>
                <span>{fmtBRL(simulation.finalAmount)}</span>
              </div>
              {simulation.installments > 1 && (
                <div className={classes.breakdownRow}>
                  <span>Parcelas</span>
                  <span>
                    {simulation.installments}× de {fmtBRL(simulation.installmentAmount)}
                  </span>
                </div>
              )}
              <Divider style={{ margin: "6px 0" }} />
              <div className={classes.breakdownRow}>
                <span>Taxa do gateway estimada</span>
                <span>− {fmtBRL(simulation.providerFeeAmount)}</span>
              </div>
              {simulation.taxAmount > 0 && (
                <div className={classes.breakdownRow}>
                  <span>Imposto estimado</span>
                  <span>− {fmtBRL(simulation.taxAmount)}</span>
                </div>
              )}
              <Divider style={{ margin: "6px 0" }} />
              <div
                className={`${classes.breakdownRow} ${classes.breakdownStrong}`}
              >
                <span>Valor líquido estimado para a empresa</span>
                <span style={{ color: "#2e7d32" }}>
                  {fmtBRL(simulation.netAmount)}
                </span>
              </div>
              <Box mt={1} display="flex" alignItems="center" style={{ gap: 8 }}>
                <Chip
                  size="small"
                  label={`Liquidação em ~${simulation.settlementDays} dia(s)`}
                  className={riskLabel?.cls || ""}
                />
                {simulation.feeSource === "default" && (
                  <Tooltip title="Taxas cadastradas ainda não existem para este provider/método. O sistema está usando uma estimativa conservadora.">
                    <Chip
                      size="small"
                      label="Taxa estimada (admin pode ajustar)"
                      color="default"
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Resultado da geração */}
        {generated && (
          <Box mt={2}>
            <Box className={classes.generatedBox}>
              <Typography variant="subtitle2">
                {generated.status === "paid"
                  ? "Pagamento confirmado"
                  : "Link gerado com sucesso"}
              </Typography>
              <Box mt={1} display="flex" alignItems="center" style={{ gap: 8 }}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={generated.paymentLink || "(sem URL direta — use PIX/boleto)"}
                  InputProps={{ readOnly: true }}
                />
                <IconButton onClick={handleCopyLink} size="small">
                  <FileCopyOutlinedIcon />
                </IconButton>
              </Box>
              {generated.pixCode && (
                <Box mt={1}>
                  <Typography variant="caption">PIX copia-e-cola:</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    value={generated.pixCode}
                    InputProps={{ readOnly: true }}
                    multiline
                    maxRows={3}
                  />
                </Box>
              )}
            </Box>
            <Typography variant="subtitle2" gutterBottom>
              Mensagem pronta para o cliente
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              multiline
              minRows={6}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {!generated && (
          <>
            <Button onClick={onClose}>Cancelar</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerate}
              disabled={
                generating ||
                !simulation ||
                simulation.__error ||
                noGateway
              }
            >
              {generating ? <CircularProgress size={18} /> : "Gerar link"}
            </Button>
          </>
        )}
        {generated && (
          <>
            <Button onClick={onClose}>Fechar</Button>
            <Button
              startIcon={<FileCopyOutlinedIcon />}
              onClick={handleCopyMessage}
            >
              Copiar mensagem
            </Button>
            <Button
              startIcon={<SendIcon />}
              variant="contained"
              color="primary"
              onClick={handleInsertInChat}
            >
              Inserir no chat
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PaymentLinkModal;
